import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { Referrers } from '../../consts/defaultConfigValues/defaultConstants'
import usePrefillCartMutation from '../../graphQL/mutations/prefillCart'
import { IPrefillCartMutationResponse } from '../../graphQL/mutations/prefillCart/types'
import { ICartProduct, ICartProductInput } from '../../interfaces'
import { setSynchronizingCart } from '../../redux/slices/catalog'
import { onCartResponse } from '../../utils/onCartResponse'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { generateDataDogContext } from '../../utils/dataDog'
import { fireGTMEvent } from '../../utils/fireGTMEvent'
import { parseAnalyticsItems } from '../../utils/getAnalyticsItems'

export type IAddPrefillCartItem = Pick<
  ICartProduct,
  'sku' | 'quantity' | 'package' | 'price' | 'discounts' | 'name'
>

interface AddPrefillCartArgs {
  items: IAddPrefillCartItem[]
  storefrontName: string
  sessionUid: string
  onCompleted?: (data: IPrefillCartMutationResponse) => void
  replaceCart?: boolean
  referrer?: Referrers
}

export const useAddPrefillCart = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const currency = useAppSelector(
    (state) => state.defaultSlice.config?.options?.currencyPrefix || '',
  )

  /**
   * Handles onComplete callback from prefill cart mutation.
   *
   * Reads the store when the response lands, not the render-time snapshot.
   * Nothing raises a loader or blocks navigation while the prefill is in
   * flight, so the shopper may have left the page by the time Apollo calls
   * this.
   *
   * A snapshot from a page that has since unmounted still holds that page's
   * lists, and `onCartResponse` would write them back over the next page's:
   * among them the catalogue list, the open product and the category-nav
   * list. Which way the category-nav list goes wrong depends on the old page.
   * One that never loads it, such as the cart, holds it as `null`, so the
   * next page's list is skipped rather than updated; a category-nav page has
   * it refilled by `useGetProductsNav`, so its own list is written back over
   * the next page's. See `utils/onCartResponse.ts` for the full set, and
   * `useDeleteProductFromCart` (`useDeleteItemFromCart.ts`) for the same
   * hazard.
   */
  const onCompletePrefillCartMutation = (
    data: IPrefillCartMutationResponse,
  ) => {
    if (data?.prefillCart) {
      onCartResponse({
        data: data.prefillCart,
        state: store.getState(),
        dispatch,
      })
    }
    dispatch(setSynchronizingCart(false))
  }

  /**
   * Handles onError callback from prefill cart mutation
   */
  const onErrorPrefillCartMutation = (error: ApolloError) => {
    dispatch(setSynchronizingCart(false))
    const context = generateDataDogContext({
      title: 'Prefill cart mutation error',
      extraInfo: { function: 'usePrefillCartMutation' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  /**
   * Get prefillCart mutation hook
   */
  const [sendPrefillCart] = usePrefillCartMutation({
    onCompleted: onCompletePrefillCartMutation,
    onError: onErrorPrefillCartMutation,
  })

  /**
   * Adds prefill cart items to cart.
   *
   * Returns the mutation payload on success and `null` on failure, because a
   * caller cannot learn that any other way. Apollo's `useMutation` only
   * rejects when no `onError` is registered; with one — as above — it
   * resolves with `{ data: undefined, errors }` instead, so a `try`/`catch`
   * around this call never sees a failed mutation.
   */
  const addPrefillCart = async ({
    items,
    storefrontName,
    sessionUid,
    onCompleted,
    replaceCart,
    referrer = Referrers.PREFILL_CART,
  }: AddPrefillCartArgs) => {
    dispatch(setSynchronizingCart(true))
    const filteredItems = items.filter((item) => item.quantity > 0)
    fireGTMEvent('commerce-add_to_cart', {
      currency,
      value: filteredItems.reduce(
        (acc, { price, quantity }) => acc + price * quantity,
        0,
      ),
      items: filteredItems.map((item, index) =>
        parseAnalyticsItems(item, index),
      ),
    })
    const itemsToAdd = filteredItems.map<ICartProductInput>((item) => ({
      sku: item.sku,
      quantity: item.quantity,
      selectedPackage: item.package?.name,
    }))

    const response = await sendPrefillCart({
      variables: {
        storefrontName,
        sessionUid,
        items: itemsToAdd,
        replaceCart,
        referrer,
      },
    })
    if (response?.data) {
      onCompleted?.(response.data)
      return response.data
    }
    return null
  }

  return { addPrefillCart }
}
